import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    name: "",
    email: "",
    project_type: "",
    quantity: "",
    deadline: "",
    message: "",
};

const ALLOWED_EXT = [".png", ".jpg", ".jpeg", ".webp", ".heic", ".heif", ".pdf", ".ai", ".psd", ".svg", ".eps", ".zip"];
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 3;

const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function CustomInquiryModal({ open, onClose }) {
    const [form, setForm] = useState(EMPTY);
    const [files, setFiles] = useState([]); // raw File objects pending upload
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!open) return null;

    const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

    const close = () => {
        onClose?.();
        setTimeout(() => {
            setForm(EMPTY);
            setFiles([]);
            setDone(false);
        }, 250);
    };

    const onFilesPicked = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = ""; // allow re-picking same file
        const errors = [];
        const next = [...files];
        for (const f of picked) {
            if (next.length >= MAX_FILES) {
                errors.push(`Max ${MAX_FILES} files total`);
                break;
            }
            const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
            if (!ALLOWED_EXT.includes(ext)) {
                errors.push(`${f.name}: ${ext} not allowed`);
                continue;
            }
            if (f.size > MAX_FILE_BYTES) {
                errors.push(`${f.name}: exceeds 25 MB`);
                continue;
            }
            // De-dupe by name+size
            if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
            next.push(f);
        }
        if (errors.length) toast.error(errors[0]);
        setFiles(next);
    };

    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            let attachments = [];
            if (files.length > 0) {
                const fd = new FormData();
                files.forEach((f) => fd.append("files", f, f.name));
                const upRes = await axios.post(`${API}/custom-inquiries/upload`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                attachments = upRes.data;
            }
            await axios.post(`${API}/custom-inquiries`, {
                ...form,
                attachments: attachments.length ? attachments : null,
            });
            setDone(true);
            toast.success("Inquiry sent — we'll get back to you within 48 hours.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't send. Try again.";
            toast.error(typeof detail === "string" ? detail : "Couldn't send.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="custom-inquiry-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={close}
        >
            <div
                className="relative w-full max-w-2xl bg-fgm-surface border border-white/10 max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="custom-inquiry-close"
                    onClick={close}
                    className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors text-2xl leading-none z-10"
                    aria-label="Close"
                >
                    ×
                </button>

                {done ? (
                    <div className="p-10 text-center">
                        <div className="font-display text-7xl text-[#E63946] mb-4">✓</div>
                        <h3 className="font-display uppercase text-3xl text-white mb-3">
                            Inquiry Received
                        </h3>
                        <p className="text-zinc-400 max-w-md mx-auto mb-6">
                            We'll review your request and reply within 48 hours
                            with quote details, timeline, and next steps.
                        </p>
                        <button
                            data-testid="custom-inquiry-done"
                            onClick={close}
                            className="btn-primary !py-3 !px-8"
                        >
                            Got It →
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-6 md:p-10">
                        <div className="fgm-stripe-divider w-24 mb-5" />
                        <div className="flex items-center gap-3 mb-2">
                            <span className="font-display text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                                Custom Apparel · Powered By Devoted Brand
                            </span>
                        </div>
                        <h3 className="font-display uppercase text-white text-3xl md:text-4xl leading-tight mb-2">
                            Submit A Custom <br />
                            <span className="font-outline-strong">Order Inquiry</span>
                        </h3>
                        <p className="text-zinc-400 text-sm mb-8">
                            Uniforms, activewear, promo apparel, team kits — drop the
                            details and we'll get back with a quote.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <Field label="Full Name" required>
                                <input
                                    data-testid="inquiry-input-name"
                                    type="text"
                                    required
                                    maxLength={120}
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your name"
                                    className="fgm-input"
                                />
                            </Field>
                            <Field label="Email" required>
                                <input
                                    data-testid="inquiry-input-email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@email.com"
                                    className="fgm-input"
                                />
                            </Field>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <Field label="Project Type" required>
                                <input
                                    data-testid="inquiry-input-project"
                                    type="text"
                                    required
                                    maxLength={120}
                                    value={form.project_type}
                                    onChange={set("project_type")}
                                    placeholder="Uniforms · Team Kit · Promo Tees…"
                                    className="fgm-input"
                                />
                            </Field>
                            <Field label="Quantity" required>
                                <input
                                    data-testid="inquiry-input-quantity"
                                    type="text"
                                    required
                                    maxLength={60}
                                    value={form.quantity}
                                    onChange={set("quantity")}
                                    placeholder="e.g. 50 shirts, 25 hoodies"
                                    className="fgm-input"
                                />
                            </Field>
                        </div>

                        <div className="mb-4">
                            <Field label="Deadline / Event Date">
                                <input
                                    data-testid="inquiry-input-deadline"
                                    type="text"
                                    maxLength={120}
                                    value={form.deadline}
                                    onChange={set("deadline")}
                                    placeholder="Flexible · or e.g. by July 15, 2026"
                                    className="fgm-input"
                                />
                            </Field>
                        </div>

                        <div className="mb-6">
                            <Field label="Project Details" required>
                                <textarea
                                    data-testid="inquiry-input-message"
                                    required
                                    rows={5}
                                    maxLength={2000}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Tell us about your project — design ideas, colors, sizes, anything that helps us quote accurately."
                                    className="fgm-input resize-none"
                                />
                            </Field>
                        </div>

                        <div className="mb-6">
                            <span className="block font-display text-[10px] tracking-[0.3em] text-zinc-400 uppercase mb-2">
                                Design Files <span className="text-zinc-600 ml-1">· Optional</span>
                            </span>
                            <label
                                htmlFor="inquiry-files-input"
                                data-testid="inquiry-files-dropzone"
                                className="block border border-dashed border-white/15 hover:border-[#E63946]/60 hover:bg-[#E63946]/5 transition-colors cursor-pointer p-5 text-center"
                            >
                                <div className="font-display text-xs tracking-[0.25em] text-white uppercase">
                                    + Attach High-Res Files
                                </div>
                                <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase mt-1">
                                    PNG · JPG · PDF · AI · PSD · SVG · ZIP · Max 3 files · 25 MB each
                                </div>
                                <input
                                    id="inquiry-files-input"
                                    data-testid="inquiry-files-input"
                                    type="file"
                                    multiple
                                    accept={ALLOWED_EXT.join(",")}
                                    onChange={onFilesPicked}
                                    className="sr-only"
                                />
                            </label>
                            {files.length > 0 && (
                                <ul
                                    data-testid="inquiry-files-list"
                                    className="mt-3 space-y-2"
                                >
                                    {files.map((f, i) => (
                                        <li
                                            key={`${f.name}-${f.size}-${i}`}
                                            data-testid={`inquiry-file-${i}`}
                                            className="flex items-center justify-between gap-3 bg-black/40 border border-white/5 px-3 py-2"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="text-[#E63946] text-sm">📎</span>
                                                <div className="min-w-0">
                                                    <div className="text-sm text-white truncate">
                                                        {f.name}
                                                    </div>
                                                    <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">
                                                        {fmtBytes(f.size)}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                data-testid={`inquiry-file-remove-${i}`}
                                                onClick={() => removeFile(i)}
                                                className="text-zinc-500 hover:text-[#E63946] text-lg leading-none px-2"
                                                aria-label={`Remove ${f.name}`}
                                            >
                                                ×
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <button
                            data-testid="inquiry-submit"
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting
                                ? files.length > 0 ? "Uploading…" : "Sending…"
                                : "Send Inquiry →"}
                        </button>
                        <p className="text-[10px] font-display tracking-[0.3em] text-zinc-500 uppercase text-center mt-3">
                            We Reply Within 48 Hours
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block font-display text-[10px] tracking-[0.3em] text-zinc-400 uppercase mb-2">
                {label}
                {required && <span className="text-[#E63946] ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}
